import Flag from '@/components/Flag'
import { CheckIcon } from '@heroicons/react/24/solid'
import { twMerge } from 'tailwind-merge'

interface Props {
  animationDelayMs: number
  emoji: string
  id?: string
  indicator: 'radio' | 'checkbox'
  isSelected: boolean
  name: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  subtitle?: string
  title: string
  value: string
}

/**
 * An option row that keeps a real `<input>` inside the step's `<Form>`, so the
 * submitted field name/value stay identical to the shared question pages this
 * variant restyles.
 */
export default function OptionButton({
  animationDelayMs,
  emoji,
  id,
  indicator,
  isSelected,
  name,
  onChange,
  subtitle,
  title,
  value,
}: Props) {
  return (
    <label
      className={twMerge(
        'hw-option animate-fade-up',
        isSelected && 'hw-option-selected',
      )}
      style={{ animationDelay: `${animationDelayMs}ms` }}
    >
      <input
        checked={indicator === 'checkbox' ? isSelected : undefined}
        className="sr-only"
        id={id}
        name={name}
        onChange={onChange}
        type={indicator}
        value={value}
      />

      <Flag emoji={emoji} size={26} />

      <span className="flex grow flex-col gap-0.5">
        <span>{title}</span>

        {subtitle && (
          <span
            className={twMerge(
              'text-[13px] leading-snug font-normal',
              isSelected ? 'text-hw-primary/70' : 'text-hw-muted',
            )}
          >
            {subtitle}
          </span>
        )}
      </span>

      {/*
        The badge only renders once the row is picked. A column of empty rings
        next to every option is the single most dated thing about a quiz step,
        and the tinted fill already carries the selected state on its own.
      */}
      {isSelected && (
        <span
          className={twMerge(
            'bg-hw-primary animate-check-pop flex h-6 w-6 shrink-0 items-center justify-center',
            indicator === 'radio' ? 'rounded-full' : 'rounded-md',
          )}
        >
          <CheckIcon className="h-4 w-4 text-white" />
        </span>
      )}
    </label>
  )
}
